import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarIcon, VideoIcon, FolderIcon, ClockIcon, UsersIcon, FileTextIcon, ImageIcon, TableIcon, ChevronRightIcon, MessageSquareIcon, CircleIcon, PhoneCallIcon, WifiIcon, MonitorIcon, MicIcon, CheckCircle2Icon, LoaderIcon } from 'lucide-react';
const todayMeetings = [{
  id: 1,
  title: 'Board Strategy Review',
  time: '10:00 AM',
  duration: '2h',
  attendees: 8,
  isNext: true,
  hasTeams: true
}, {
  id: 2,
  title: 'Product Launch Planning',
  time: '2:00 PM',
  duration: '1h',
  attendees: 5,
  isNext: false,
  hasTeams: true
}, {
  id: 3,
  title: 'Investor Call — Q4 Update',
  time: '4:30 PM',
  duration: '45m',
  attendees: 3,
  isNext: false,
  hasTeams: false
}];
const recentFiles = [{
  id: 1,
  name: 'Q4 Financial Report.xlsx',
  type: 'excel',
  modified: '2 hours ago'
}, {
  id: 2,
  name: 'Board Presentation.pptx',
  type: 'powerpoint',
  modified: '5 hours ago'
}, {
  id: 3,
  name: 'Strategic Plan 2025.docx',
  type: 'word',
  modified: 'Yesterday'
}, {
  id: 4,
  name: 'Market Analysis.pdf',
  type: 'pdf',
  modified: 'Yesterday'
}];
const teamChats = [{
  id: 1,
  name: 'Executive Team',
  message: 'Budget approved ✓',
  time: '9:12 AM',
  unread: 2
}, {
  id: 2,
  name: 'Priya Sharma',
  message: 'Your 2 PM has been moved...',
  time: '8:45 AM',
  unread: 0
}, {
  id: 3,
  name: 'Product Team',
  message: 'Roadmap deck is ready',
  time: 'Yesterday',
  unread: 5
}];
const getFileIcon = (type: string) => {
  switch (type) {
    case 'excel':
      return <TableIcon className="w-4 h-4 text-green-400" data-id="element-317" />;
    case 'powerpoint':
      return <ImageIcon className="w-4 h-4 text-orange-400" data-id="element-318" />;
    case 'word':
      return <FileTextIcon className="w-4 h-4 text-blue-400" data-id="element-319" />;
    default:
      return <FileTextIcon className="w-4 h-4 text-red-400" data-id="element-320" />;
  }
};
type JoinStage = 'idle' | 'setting-up' | 'joining' | 'connected';
const setupSteps = [{
  id: 1,
  label: 'Configuring room display',
  icon: <MonitorIcon className="w-4 h-4" data-id="element-321" />
}, {
  id: 2,
  label: 'Activating microphones & speakers',
  icon: <MicIcon className="w-4 h-4" data-id="element-322" />
}, {
  id: 3,
  label: 'Setting privacy glass',
  icon: <WifiIcon className="w-4 h-4" data-id="element-323" />
}, {
  id: 4,
  label: 'Connecting to Teams',
  icon: <VideoIcon className="w-4 h-4" data-id="element-324" />
}];
export function O365Widgets() {
  const [joinStage, setJoinStage] = useState<JoinStage>('idle');
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [joiningMeeting, setJoiningMeeting] = useState<string>('');
  const handleJoinTeams = (meetingTitle: string) => {
    setJoiningMeeting(meetingTitle);
    setJoinStage('setting-up');
    setCompletedSteps([]);
    // Simulate setup steps completing one by one
    setupSteps.forEach((step, i) => {
      setTimeout(() => {
        setCompletedSteps(prev => [...prev, step.id]);
        if (i === setupSteps.length - 1) {
          setTimeout(() => setJoinStage('joining'), 400);
          setTimeout(() => setJoinStage('connected'), 2200);
        }
      }, 700 * (i + 1));
    });
  };
  const handleDismiss = () => {
    setJoinStage('idle');
    setCompletedSteps([]);
    setJoiningMeeting('');
  };
  return <>
      {/* Teams Setup Overlay */}
      <AnimatePresence data-id="element-325">
        {joinStage !== 'idle' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center" style={{
        background: 'rgba(13,15,20,0.92)',
        backdropFilter: 'blur(16px)'
      }} data-id="element-326">
            <motion.div initial={{
          scale: 0.92,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.92,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 180,
          damping: 22
        }} className="w-full max-w-lg rounded-3xl p-10 text-center" style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(201,168,76,0.25)'
        }} data-id="element-327">
              {/* Teams icon */}
              <div className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl" style={{
            background: 'linear-gradient(135deg, #6264A7, #464775)'
          }} data-id="element-328">
                <VideoIcon className="w-10 h-10 text-white" data-id="element-329" />
              </div>

              <AnimatePresence mode="wait" data-id="element-330">
                {joinStage === 'connected' ? <motion.div key="connected" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center" data-id="element-331">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200
              }} className="w-16 h-16 rounded-full bg-[rgba(34,197,94,0.15)] border-2 border-green-500 flex items-center justify-center mb-4" data-id="element-332">
                      <CheckCircle2Icon className="w-8 h-8 text-green-400" data-id="element-333" />
                    </motion.div>
                    <h2 className="text-2xl font-semibold text-white mb-2" style={{
                fontFamily: "'Playfair Display', serif"
              }} data-id="element-334">
                      You're in the meeting
                    </h2>
                    <p className="text-gray-400 text-sm mb-1" data-id="element-335">
                      {joiningMeeting}
                    </p>
                    <p className="text-green-400 text-xs mb-8" data-id="element-336">
                      Room environment configured · All systems ready
                    </p>
                    <button onClick={handleDismiss} className="h-12 px-8 rounded-xl font-semibold text-sm transition-all" style={{
                background: 'linear-gradient(135deg, #C9A84C, #D4AF37)',
                color: '#0D0F14'
              }} data-id="element-337">
                      Continue to Dashboard
                    </button>
                  </motion.div> : joinStage === 'joining' ? <motion.div key="joining" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex flex-col items-center" data-id="element-338">
                    <h2 className="text-2xl font-semibold text-white mb-2" style={{
                fontFamily: "'Playfair Display', serif"
              }} data-id="element-339">
                      Joining Teams Call
                    </h2>
                    <p className="text-gray-400 text-sm mb-6" data-id="element-340">
                      {joiningMeeting}
                    </p>
                    <div className="flex items-center gap-2 text-purple-300" data-id="element-341">
                      <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-342" />
                      <span className="text-sm" data-id="element-343">Connecting...</span>
                    </div>
                  </motion.div> : <motion.div key="setup" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} data-id="element-344">
                    <h2 className="text-2xl font-semibold text-white mb-1" style={{
                fontFamily: "'Playfair Display', serif"
              }} data-id="element-345">
                      Setting Up Meeting Room
                    </h2>
                    <p className="text-gray-400 text-sm mb-8" data-id="element-346">
                      {joiningMeeting}
                    </p>

                    <div className="space-y-3 text-left mb-8" data-id="element-347">
                      {setupSteps.map((step, i) => {
                  const isDone = completedSteps.includes(step.id);
                  const isActive = !isDone && completedSteps.length === i;
                  return <motion.div key={step.id} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: i * 0.1
                  }} className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${isDone ? 'bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.2)]' : isActive ? 'bg-[rgba(201,168,76,0.08)] border border-[rgba(201,168,76,0.2)]' : 'bg-[rgba(255,255,255,0.02)] border border-transparent'}`} data-id="element-348">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDone ? 'text-green-400' : isActive ? 'text-[#C9A84C]' : 'text-gray-600'}`} data-id="element-349">
                              {isDone ? <CheckCircle2Icon className="w-5 h-5" data-id="element-350" /> : isActive ? <motion.div animate={{
                        rotate: 360
                      }} transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: 'linear'
                      }} data-id="element-351">
                                  <LoaderIcon className="w-5 h-5" data-id="element-352" />
                                </motion.div> : step.icon}
                            </div>
                            <span className={`text-sm font-medium ${isDone ? 'text-green-300' : isActive ? 'text-white' : 'text-gray-500'}`} data-id="element-353">
                              {step.label}
                            </span>
                            {isDone && <motion.span initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} className="ml-auto text-xs text-green-500" data-id="element-354">
                                Done
                              </motion.span>}
                          </motion.div>;
                })}
                    </div>

                    {/* Progress bar */}
                    <div className="w-full h-1.5 rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden" data-id="element-355">
                      <motion.div className="h-full rounded-full" style={{
                  background: 'linear-gradient(90deg, #C9A84C, #D4AF37)'
                }} animate={{
                  width: `${completedSteps.length / setupSteps.length * 100}%`
                }} transition={{
                  duration: 0.4
                }} data-id="element-356" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex flex-col gap-4 h-full" data-id="element-357">
        {/* ── MERGED: Calendar + Teams ── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="rounded-2xl overflow-hidden flex-1" style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-358">
          {/* Card Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[rgba(201,168,76,0.12)]" data-id="element-359">
            <div className="flex items-center gap-3" data-id="element-360">
              <div className="flex -space-x-1.5" data-id="element-361">
                <div className="w-8 h-8 rounded-lg bg-[rgba(59,130,246,0.2)] flex items-center justify-center z-10" data-id="element-362">
                  <CalendarIcon className="w-4 h-4 text-blue-400" data-id="element-363" />
                </div>
                <div className="w-8 h-8 rounded-lg bg-[rgba(139,92,246,0.2)] flex items-center justify-center" data-id="element-364">
                  <VideoIcon className="w-4 h-4 text-purple-400" data-id="element-365" />
                </div>
              </div>
              <div data-id="element-366">
                <h3 className="text-base font-semibold text-white" style={{
                fontFamily: "'Playfair Display', serif"
              }} data-id="element-367">
                  Schedule & Teams
                </h3>
                <p className="text-xs text-gray-400 mt-0.5" data-id="element-368">
                  {todayMeetings.length} meetings today
                </p>
              </div>
            </div>
            <button className="flex items-center gap-1.5 text-xs text-[#C9A84C] hover:text-[#D4AF37] transition-colors" data-id="element-369">
              Full Calendar <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-370" />
            </button>
          </div>

          {/* Meetings List */}
          <div className="px-4 py-3 space-y-2" data-id="element-371">
            {todayMeetings.map((meeting, index) => <motion.div key={meeting.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.08 * index
          }} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${meeting.isNext ? 'bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.25)]' : 'bg-[rgba(255,255,255,0.025)] hover:bg-[rgba(255,255,255,0.05)]'}`} data-id="element-372">
                <div className="w-16 flex-shrink-0 text-center" data-id="element-373">
                  <p className={`text-xs font-semibold ${meeting.isNext ? 'text-blue-300' : 'text-gray-400'}`} data-id="element-374">
                    {meeting.time}
                  </p>
                  <p className="text-xs text-gray-600" data-id="element-375">{meeting.duration}</p>
                </div>
                <div className={`w-px self-stretch ${meeting.isNext ? 'bg-blue-500/40' : 'bg-[rgba(255,255,255,0.08)]'}`} data-id="element-376" />
                <div className="flex-1 min-w-0" data-id="element-377">
                  <div className="flex items-center gap-2" data-id="element-378">
                    <p className={`text-sm font-medium truncate ${meeting.isNext ? 'text-white' : 'text-gray-300'}`} data-id="element-379">
                      {meeting.title}
                    </p>
                    {meeting.isNext && <span className="flex-shrink-0 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full" data-id="element-380">
                        Next
                      </span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500" data-id="element-381">
                    <span className="flex items-center gap-1" data-id="element-382">
                      <UsersIcon className="w-3 h-3" data-id="element-383" />
                      {meeting.attendees} attendees
                    </span>
                  </div>
                </div>
                {meeting.hasTeams && <button onClick={() => handleJoinTeams(meeting.title)} className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${meeting.isNext ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-500/20' : 'bg-[rgba(139,92,246,0.15)] hover:bg-[rgba(139,92,246,0.25)] text-purple-300'}`} data-id="element-384">
                    <VideoIcon className="w-3.5 h-3.5" data-id="element-385" />
                    {meeting.isNext ? 'Join Now' : 'Join'}
                  </button>}
                {!meeting.hasTeams && <button className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] text-gray-400 transition-colors" data-id="element-386">
                    <PhoneCallIcon className="w-3.5 h-3.5" data-id="element-387" />
                    Dial In
                  </button>}
              </motion.div>)}
          </div>

          {/* Teams Quick Actions */}
          <div className="px-4 pb-4 pt-1" data-id="element-388">
            <div className="rounded-xl bg-[rgba(139,92,246,0.08)] border border-[rgba(139,92,246,0.2)] p-3" data-id="element-389">
              <div className="flex items-center justify-between mb-3" data-id="element-390">
                <div className="flex items-center gap-2" data-id="element-391">
                  <VideoIcon className="w-4 h-4 text-purple-400" data-id="element-392" />
                  <span className="text-sm font-medium text-purple-300" data-id="element-393">
                    Microsoft Teams
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-394">
                  <CircleIcon className="w-2 h-2 text-green-400 fill-green-400" data-id="element-395" />
                  <span className="text-xs text-green-400" data-id="element-396">Available</span>
                </div>
              </div>
              <div className="space-y-2 mb-3" data-id="element-397">
                {teamChats.map(chat => <button key={chat.id} className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-colors text-left" data-id="element-398">
                    <div className="w-7 h-7 rounded-full bg-[rgba(139,92,246,0.3)] flex items-center justify-center flex-shrink-0" data-id="element-399">
                      <MessageSquareIcon className="w-3.5 h-3.5 text-purple-300" data-id="element-400" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-401">
                      <p className="text-xs font-medium text-gray-200 truncate" data-id="element-402">
                        {chat.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate" data-id="element-403">
                        {chat.message}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0" data-id="element-404">
                      <span className="text-xs text-gray-600" data-id="element-405">{chat.time}</span>
                      {chat.unread > 0 && <span className="w-4 h-4 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center leading-none" data-id="element-406">
                          {chat.unread}
                        </span>}
                    </div>
                  </button>)}
              </div>
              <div className="flex gap-2" data-id="element-407">
                <button onClick={() => handleJoinTeams('Instant Meeting')} className="flex-1 h-9 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-1.5" data-id="element-408">
                  <VideoIcon className="w-3.5 h-3.5" data-id="element-409" /> New Meeting
                </button>
                <button className="flex-1 h-9 rounded-lg bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.1)] text-gray-300 text-xs font-medium transition-colors flex items-center justify-center gap-1.5" data-id="element-410">
                  <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-411" /> Open Teams
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── OneDrive ── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="rounded-2xl overflow-hidden" style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-412">
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[rgba(201,168,76,0.1)]" data-id="element-413">
            <div className="flex items-center gap-3" data-id="element-414">
              <div className="w-8 h-8 rounded-lg bg-[rgba(34,197,94,0.15)] flex items-center justify-center" data-id="element-415">
                <FolderIcon className="w-4 h-4 text-green-400" data-id="element-416" />
              </div>
              <h3 className="text-sm font-semibold text-white" style={{
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-417">
                OneDrive
              </h3>
            </div>
            <button className="text-xs text-[#C9A84C] hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-id="element-418">
              View All <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-419" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 p-4" data-id="element-420">
            {recentFiles.map((file, index) => <motion.button key={file.id} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.08 * index
          }} className="flex items-center gap-2.5 p-3 rounded-xl bg-[rgba(255,255,255,0.025)] hover:bg-[rgba(255,255,255,0.06)] transition-colors text-left" data-id="element-421">
                <div className="w-9 h-9 rounded-lg bg-[rgba(255,255,255,0.06)] flex items-center justify-center flex-shrink-0" data-id="element-422">
                  {getFileIcon(file.type)}
                </div>
                <div className="min-w-0 flex-1" data-id="element-423">
                  <p className="text-xs font-medium text-gray-200 truncate" data-id="element-424">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-425">
                    {file.modified}
                  </p>
                </div>
              </motion.button>)}
          </div>
        </motion.div>
      </div>
    </>;
}